import React, { useState } from 'react';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { Box, Typography, useTheme } from '@mui/material';
import { demoIcon } from '@europrocurement/l2d-icons';
import {
    ItemActions,
    LinkButton,
    ModificationDate,
    TooltipedIcon,
} from '@europrocurement/flexy-components';
import { DossierStatus } from '../../liste';
import { DOSSIER_STATUS_SUSPENDED } from '../../../constants/status';
import { ClientReference } from '../DetailDossierTitle/fragments/ClientReference';

type DossierSmallCardProps = {
    dossier: DossierOpenSearch;
    isCurrentDossier: boolean;
    link?: string;
    mulitpleDossier?: boolean;
};
const DossierSmallCard: React.FunctionComponent<DossierSmallCardProps> = function ({
    dossier,
    isCurrentDossier = false,
    link,
    mulitpleDossier = false,
}) {
    const { palette } = useTheme();
    const [numeroHovered, setNumeroHovered] = useState(false);

    if (!dossier) {
        return null;
    }

    const isV1 = dossier.numero?.startsWith('LDF') ?? false;

    return (
        <Box
            data-testid="dossier_small_card"
            sx={{
                backgroundColor: palette.background.paper,
                borderRadius: '8px',
                padding: '14px',
                border: `1px solid ${palette.grey[200]} `,
                borderRight: isCurrentDossier
                    ? `4px solid ${palette.primary.main}`
                    : `1px solid ${palette.grey[200]} `,
                gap: '4px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                boxShadow: `0px 2px 2.5px 0px ${palette.grey[200]}`,
                opacity: dossier.deleted ? 0.4 : 1,
            }}
        >
            {link && (
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    onMouseEnter={() => setNumeroHovered(true)}
                    onMouseLeave={() => setNumeroHovered(false)}
                >
                    {dossier.demo && (
                        <TooltipedIcon
                            icon={demoIcon}
                            tooltip="Démo"
                            size="xs"
                            color="error"
                        />
                    )}
                    <LinkButton
                        to={!dossier.deleted ? link : '#'}
                        onClick={() => {}}
                        wrapperSx={{
                            backgroundColor: palette.grey[100],
                            '&:hover': {
                                backgroundColor: palette.grey[200],
                                color: palette.grey[900],
                            },
                            color: palette.grey[700],
                            padding: '6px 8px',
                        }}
                        label={`${dossier.numero as string}`}
                    />
                    <ItemActions
                        copyValue={dossier.numero != null ? String(dossier.numero) : undefined}
                        visible={numeroHovered}
                        slideDirection="left"
                    />
                </Box>
            )}
            {mulitpleDossier && (
                <ClientReference
                    societeRef={dossier.societeRef as string}
                    dossierId={dossier.id as number}
                />
            )}

            {dossier.formalites?.map((formalite) => (
                <Typography
                    key={formalite?.id}
                    variant="l2dbodyxs"
                >
                    {formalite?.libelle}
                </Typography>
            ))}

            <DossierStatus
                label={dossier.deleted ? 'Supprimé' : (dossier.statutLibelle as string)}
                statutId={dossier.deleted ? DOSSIER_STATUS_SUSPENDED : (dossier.statutId as number)}
                source="BACK"
            />

            {isV1 && dossier.societeDenomination && (
                <Typography
                    variant="l2dbodyxs"
                    sx={{ opacity: 0.5 }}
                >{`Dénomination V1: ${dossier.societeDenomination}`}</Typography>
            )}
            {isV1 && dossier.societeSiren && (
                <Typography
                    variant="l2dbodyxs"
                    sx={{ opacity: 0.5 }}
                >{`Siren dossier V1: ${dossier.societeSiren}`}</Typography>
            )}
            {isV1 && dossier.affaire && (
                <Typography
                    variant="l2dbodyxs"
                    sx={{ opacity: 0.5 }}
                >{`Affaire dossier V1: ${dossier.affaire}`}</Typography>
            )}
            {isV1 && dossier.formaliteLibAutre && (
                <Typography
                    variant="l2dbodyxs"
                    sx={{ opacity: 0.5 }}
                >{`Libellé formalité V1: ${dossier.formaliteLibAutre}`}</Typography>
            )}
            {dossier && (
                <>
                    <ModificationDate
                        date={new Date(dossier.dateCreation as string)}
                        displayIcon={false}
                        isCreationDate
                        sxProps={{ opacity: 0.5, whiteSpace: 'nowrap' }}
                        sxText={{ fontSize: '0.7rem' }}
                    />
                    <ModificationDate
                        date={new Date(dossier.dateUpdate as string)}
                        displayIcon={false}
                        isCreationDate={false}
                        sxProps={{ opacity: 0.5, whiteSpace: 'nowrap' }}
                        sxText={{ fontSize: '0.7rem' }}
                    />
                </>
            )}
        </Box>
    );
};

export default DossierSmallCard;
